// Unique per page load, so the tab/panel ids stay distinct when several Letters
// blocks share one page.
let instance = 0;

export function initLetters(): void {
    document.querySelectorAll<HTMLElement>('[data-swl-letters]').forEach(root => {
        const allTabs = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-swl-letters-tab]'));
        const allPanels = Array.from(root.querySelectorAll<HTMLElement>('[data-swl-letters-panel]'));

        if (allTabs.length === 0 || allPanels.length === 0) {
            return;
        }

        // Operate on a 1:1 paired slice so a tab without a matching panel (or vice
        // versa) can never be selected or focused via click/keyboard navigation.
        const count = Math.min(allTabs.length, allPanels.length);
        const tabs = allTabs.slice(0, count);
        const panels = allPanels.slice(0, count);

        // Flag the widget as script-enhanced. Without JS the panels have no `hidden`
        // toggling, so the CSS fallback shows only the first panel; once we take over
        // here, this class lifts that fallback and select() drives visibility instead.
        root.classList.add('is-enhanced');

        // Wire each tab to its panel (aria-controls ↔ aria-labelledby) so the
        // markup becomes a complete WAI-ARIA tabs widget at runtime.
        const base = `swl-letters-${instance++}`;

        tabs.forEach((tab, i) => {
            const panel = panels[i];

            if (!panel) {
                return;
            }

            tab.id = `${base}-tab-${i}`;
            panel.id = `${base}-panel-${i}`;
            tab.setAttribute('aria-controls', panel.id);
            panel.setAttribute('aria-labelledby', tab.id);
        });

        const select = (index: number): void => {
            tabs.forEach((tab, i) => {
                const selected = i === index;

                tab.classList.toggle('is-active', selected);
                tab.setAttribute('aria-selected', String(selected));
                // Roving tabindex: only the active tab is in the tab order.
                tab.tabIndex = selected ? 0 : -1;
            });

            panels.forEach((panel, i) => {
                panel.classList.toggle('is-active', i === index);
                panel.toggleAttribute('hidden', i !== index);
            });
        };

        tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => select(i));

            tab.addEventListener('keydown', event => {
                const last = tabs.length - 1;
                let next: number;

                switch (event.key) {
                    case 'ArrowRight':
                    case 'ArrowDown':
                        next = i === last ? 0 : i + 1;
                        break;
                    case 'ArrowLeft':
                    case 'ArrowUp':
                        next = i === 0 ? last : i - 1;
                        break;
                    case 'Home':
                        next = 0;
                        break;
                    case 'End':
                        next = last;
                        break;
                    default:
                        return;
                }

                // Automatic activation: selection follows focus.
                event.preventDefault();
                select(next);
                tabs[next].focus();
            });
        });

        select(0);
    });
}
